import { Close, Email } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import dayjs from "dayjs";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import * as TinyMCETypes from "tinymce";
import {
  useMessageDialogHandlers,
  useMessageDialogState,
} from "../../App/State/Hooks/MessageDialog/useMessageDialog";
import { useMessageInputHandlers } from "../../App/State/Hooks/MessageDialog/useMessageInput";
import { useMessageScheduleState } from "../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useUserProfileStore } from "../../App/State/Stores/UserProfileStore";
import { ICandidate } from "../../App/Typings/Candidate";
import { ICandidateProfile } from "../../App/Typings/CandidateProfile";
import { ICandidateWithMessageInfo, IMessageDetail, IMessageSendPost } from "../../App/Typings/MessageText";
import { useGroupCandidateSelectionContext } from "../../GroupProfile/State/GroupCandidateSelectionContext";
import { useManageCandidateSelectionContext } from "../../ManageCandidates/State/ManageCandidateContext";
import {
  BuildDirectMessageDialogHeader,
  BuildEmailAddress,
  getMessageContainerDisplayName,
} from "../FunctionMethods/MessageFunctions";
import { NoEmailAlert, UnsubscribedAlert } from "./Components/Alerts";
import EmailMessageSendOrSchedule from "./Components/EmailMessageSendOrSchedule";
import MessageEmailHistoryTabs from "./Components/MessageEmailHistoryTabs";
import useMessageMutationState from "./State/useMessageMutationState";

export default function MessageDialogCandidateEmail({
  candidate,
  replyEmail,
  disableScheduling = false,
}: {
  candidate?: ICandidate | ICandidateProfile | ICandidateWithMessageInfo;
  replyEmail?: IMessageDetail;
  disableScheduling?: boolean;
}) {
  const {
    getters: { actionCandidate: candidateFromManageContext },
  } = useManageCandidateSelectionContext();
  const {
    getters: { actionCandidate: candidateFromGroupContext },
  } = useGroupCandidateSelectionContext();

  candidate = useMemo(
    () =>
      candidateFromGroupContext
        ? candidateFromGroupContext
        : candidateFromManageContext
          ? candidateFromManageContext
          : candidate,
    [candidateFromGroupContext, candidateFromManageContext],
  );

  const { profile } = useUserProfileStore();
  const [notifyMe, setNotifyMe] = useState(profile.notifyOnEmailReply ?? false);
  const editorRef = useRef<TinyMCETypes.Editor | null>(null);
  const { isCandidateEmailOpen, containerType, dialogTitle } = useMessageDialogState();
  const { handleCloseDialog } = useMessageDialogHandlers();
  const handleCloseDialogAndReset = () => {
    handleCloseDialog();
    setNotifyMe(profile.notifyOnEmailReply ?? false);
  };
  const { handleInputSubjectChange, handleInputEmailChange } = useMessageInputHandlers();

  const messageScheduleState = useMessageScheduleState();

  const sendMessageBody: IMessageSendPost = useMemo(
    () => ({
      candidates: candidate ? [candidate.id] : [],
      isEmail: true,
      isText: false,
      isGroup: false,
      messages: messageScheduleState.msgListQueue,
      uiComponent: "overrides in messagequeries",
      userId: "overrides in messagequeries",
      userName: "",
      replyMessageId: replyEmail?.replyToMessageId ?? "",
      notify: replyEmail?.userHasReplyNotification === true ? false : notifyMe,
      notifyEmail: "overrides in messagequeries",
    }),
    [
      replyEmail?.replyToMessageId,
      replyEmail?.userHasReplyNotification,
      JSON.stringify(messageScheduleState.msgListQueue),
      candidate?.id,
      notifyMe,
    ],
  );

  const messageMutationState = useMessageMutationState(sendMessageBody);

  const hasEmailAddresses = Boolean(
    candidate?.emailAddresses?.length && candidate.emailAddresses.every((email) => !!email.emailAddress),
  );

  const isUnsubscribed = Boolean(candidate?.unsubscribed);

  const messageDialogTitle = dialogTitle || getMessageContainerDisplayName(containerType);

  let DialogContentDisplay = (
    <EmailMessageSendOrSchedule
      editorRef={editorRef}
      messageMutationState={messageMutationState}
      isGroup={false}
      notifyMe={notifyMe}
      setNotifyMe={setNotifyMe}
      disableScheduling={disableScheduling}
      showCount={false}
      disableNotificationToggle={replyEmail?.userHasReplyNotification === true}
    />
  );
  if (!hasEmailAddresses) {
    DialogContentDisplay = <NoEmailAlert />;
  } else if (isUnsubscribed) {
    DialogContentDisplay = <UnsubscribedAlert />;
  }

  useEffect(() => {
    if (isCandidateEmailOpen && !replyEmail) {
      setNotifyMe(profile.notifyOnEmailReply ?? false);
    }
  }, [isCandidateEmailOpen, profile.notifyOnEmailReply, replyEmail]);

  useEffect(() => {
    if (isCandidateEmailOpen && replyEmail) {
      handleInputSubjectChange("Re: " + replyEmail.subject);
      const replyText = `<br/><div dir="ltr">On ${dayjs(replyEmail.sentDate).format(
        "ddd, MMM D, YYYY [at] h:mm A",
      )} ${replyEmail?.fromName} &lt;<a href="mailto:${replyEmail.fromAddress}">${
        replyEmail.fromAddress
      }</a>&gt; wrote:</div><blockquote>${replyEmail.message}</blockquote>`;
      handleInputEmailChange(
        {
          body: replyText,
          bodyText: replyText,
        },
        editorRef,
      );
      setNotifyMe(replyEmail.userHasReplyNotification === true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(replyEmail), isCandidateEmailOpen]);

  useEffect(() => {
    return () => {
      handleCloseDialogAndReset();
    };
  }, []);

  return (
    <Dialog
      id="message-dialog"
      aria-labelledby="message-dialog-title"
      open={isCandidateEmailOpen}
      onClose={handleCloseDialogAndReset}
      scroll="paper"
      fullWidth={true}
      maxWidth="lg"
      disableEnforceFocus={true}
      TransitionProps={{ unmountOnExit: true }}
      PaperProps={{
        sx: {
          maxHeight: { xs: "calc(100vh - 1rem)", sm: "calc(100vh - 64px)" },
          height: { xs: "100vh", sm: "90vh" },
          width: { xs: "calc(100% - 1rem)", sm: "calc(100% - 2rem)" },
          m: { xs: "1rem", sm: "2rem" },
        },
      }}
    >
      <DialogTitle
        id="message-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
          px: { xs: 2, md: 4 },
          color: "white",
          backgroundColor: "#00af4a",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
          <Box>{messageDialogTitle + ": " + BuildDirectMessageDialogHeader(candidate)}</Box>
          <Box>
            <Email />
            {BuildEmailAddress(candidate)}
          </Box>
        </Box>
        <Box sx={{ ml: { xs: "auto", md: 0 } }}>
          <IconButton
            color="inherit"
            onClick={handleCloseDialogAndReset}
            aria-label="Close Direct Email Message Window"
            aria-controls="message-dialog"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers={true}
        sx={{
          display: "grid",
          gridTemplateColumns: "100%",
          gridTemplateRows: "auto 1fr",
          height: "100%",
        }}
      >
        {DialogContentDisplay}
        <MessageEmailHistoryTabs candidates={candidate ? [candidate.id] : []} />
      </DialogContent>

      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button
          style={{ marginTop: "20px" }}
          variant={"outlined"}
          color="primary"
          onClick={handleCloseDialogAndReset}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
